import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Wrench,
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Bell,
  Send
} from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const WorkOrders = () => {
  const { token, isManager } = useAuth();
  const [workOrders, setWorkOrders] = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [deleteOrder, setDeleteOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  const [formData, setFormData] = useState({
    property_id: "",
    tenant_id: "",
    title: "",
    description: "",
    priority: "medium",
    status: "open",
    assigned_to: "",
    estimated_cost: "",
    actual_cost: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [ordersRes, propertiesRes, tenantsRes] = await Promise.all([
        axios.get(`${API}/work-orders`, { headers }),
        axios.get(`${API}/properties`, { headers }),
        axios.get(`${API}/tenants`, { headers })
      ]);
      setWorkOrders(ordersRes.data);
      setProperties(propertiesRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      toast.error("Failed to load work orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const resetForm = () => {
    setFormData({
      property_id: "",
      tenant_id: "",
      title: "",
      description: "",
      priority: "medium",
      status: "open",
      assigned_to: "",
      estimated_cost: "",
      actual_cost: ""
    });
    setEditingOrder(null);
  };

  const handleOpenDialog = (order = null) => {
    if (order) {
      setEditingOrder(order);
      setFormData({
        property_id: order.property_id,
        tenant_id: order.tenant_id || "",
        title: order.title,
        description: order.description,
        priority: order.priority,
        status: order.status,
        assigned_to: order.assigned_to || "",
        estimated_cost: order.estimated_cost?.toString() || "",
        actual_cost: order.actual_cost?.toString() || ""
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.property_id || !formData.title || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }

    const submitData = {
      ...formData,
      tenant_id: formData.tenant_id || null,
      estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
      actual_cost: formData.actual_cost ? parseFloat(formData.actual_cost) : null
    };

    setSaving(true);
    try {
      if (editingOrder) {
        await axios.put(`${API}/work-orders/${editingOrder.id}`, submitData, { headers });
        toast.success("Work order updated successfully");
      } else {
        await axios.post(`${API}/work-orders`, submitData, { headers });
        toast.success("Work order created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save work order");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      await axios.put(`${API}/work-orders/${orderId}/status?status=${status}`, {}, { headers });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/work-orders/${deleteOrder.id}`, { headers });
      toast.success("Work order deleted");
      setDeleteOrder(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete work order");
    }
  };

  const handleNotify = async (orderId) => {
    try {
      const response = await axios.post(`${API}/work-orders/${orderId}/notify`, {}, { headers });
      if (response.data.owner?.status === "sent") {
        toast.success("Notifications sent to owner!");
      } else if (response.data.assigned_to?.status === "info") {
        toast.info(response.data.assigned_to.message);
      } else {
        toast.success("Notification triggered");
      }
    } catch (error) {
      toast.error("Failed to send notifications");
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: { class: "bg-red-100 text-red-700", icon: AlertTriangle },
      high: { class: "bg-orange-100 text-orange-700", icon: AlertTriangle },
      medium: { class: "bg-yellow-50 text-yellow-700", icon: Clock },
      low: { class: "bg-slate-100 text-slate-600", icon: Clock }
    };
    return styles[priority] || styles.medium;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { class: "bg-blue-100 text-blue-700", icon: Clock },
      in_progress: { class: "bg-yellow-50 text-yellow-700", icon: Loader2 },
      completed: { class: "bg-green-100 text-green-700", icon: CheckCircle },
      cancelled: { class: "bg-slate-100 text-slate-500", icon: XCircle }
    };
    return styles[status] || styles.open;
  };

  const filteredOrders = workOrders.filter(order => {
    const matchesSearch = 
      order.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.property?.address?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "open") return matchesSearch && (order.status === "open" || order.status === "in_progress");
    if (activeTab === "completed") return matchesSearch && order.status === "completed";
    return matchesSearch;
  });

  const openCount = workOrders.filter(o => o.status === "open" || o.status === "in_progress").length;
  const completedCount = workOrders.filter(o => o.status === "completed").length;

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="skeleton h-8 w-48" />
        </div>
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="work-orders-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Work Orders</h1>
          <p className="text-slate-500 mt-1">
            {openCount} open, {completedCount} completed
          </p>
        </div>
        {isManager && (
          <Button onClick={() => handleOpenDialog()} className="bg-green-700 hover:bg-green-800" data-testid="create-work-order-button">
            <Plus className="w-4 h-4 mr-2" />
            Create Work Order
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="all">All ({workOrders.length})</TabsTrigger>
            <TabsTrigger value="open">Open ({openCount})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({completedCount})</TabsTrigger>
          </TabsList>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search work orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="work-order-search-input"
            />
          </div>
        </div>

        <TabsContent value={activeTab}>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 mb-4">No work orders found</p>
              {isManager && (
                <Button onClick={() => handleOpenDialog()} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create first work order
                </Button>
              )}
            </div>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    {isManager && <TableHead className="w-24">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => {
                    const priorityStyle = getPriorityBadge(order.priority);
                    const statusStyle = getStatusBadge(order.status);
                    
                    return (
                      <TableRow key={order.id} data-testid={`work-order-row-${order.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{order.title}</p>
                            <p className="text-sm text-slate-500 truncate max-w-xs">
                              {order.description}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {order.property?.address || "N/A"}
                        </TableCell>
                        <TableCell>
                          <Badge className={priorityStyle.class}>
                            {order.priority}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isManager ? (
                            <Select
                              value={order.status}
                              onValueChange={(value) => handleStatusChange(order.id, value)}
                            >
                              <SelectTrigger className="h-8 w-32">
                                <Badge className={statusStyle.class}>
                                  {order.status.replace("_", " ")}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="open">Open</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={statusStyle.class}>
                              {order.status.replace("_", " ")}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>{order.assigned_to || "-"}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {order.actual_cost ? (
                            <span className="text-red-600">${order.actual_cost.toLocaleString()}</span>
                          ) : order.estimated_cost ? (
                            <span className="text-slate-500">~${order.estimated_cost.toLocaleString()}</span>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        {isManager && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleNotify(order.id)}
                                title="Send notifications"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Bell className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleOpenDialog(order)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setDeleteOrder(order)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingOrder ? "Edit Work Order" : "Create Work Order"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Property *</Label>
              <Select
                value={formData.property_id}
                onValueChange={(value) => setFormData({ ...formData, property_id: value })}
              >
                <SelectTrigger data-testid="work-order-property-select">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((property) => (
                    <SelectItem key={property.id} value={property.id}>
                      {property.name} - {property.address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tenant (Optional)</Label>
              <Select
                value={formData.tenant_id}
                onValueChange={(value) => setFormData({ ...formData, tenant_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Fix leaking faucet"
                data-testid="work-order-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the issue or work needed..."
                rows={3}
                data-testid="work-order-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value })}
                >
                  <SelectTrigger data-testid="work-order-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Input
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                placeholder="Name of person or company"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estimated Cost</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.estimated_cost}
                  onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>

              <div className="space-y-2">
                <Label>Actual Cost</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.actual_cost}
                  onChange={(e) => setFormData({ ...formData, actual_cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" disabled={saving} data-testid="save-work-order-button">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingOrder ? "Save Changes" : "Create Work Order"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteOrder} onOpenChange={() => setDeleteOrder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Work Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteOrder?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WorkOrders;
